import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Landmark } from "lucide-react";
import api, { formatApiError, downloadPdf } from "../lib/api";
import { DataTable, StatusBadge, fmt } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function ParentPortal() {
  const [data, setData] = useState(null);
  const [payInvoice, setPayInvoice] = useState(null);
  const [form, setForm] = useState({ method: "", bank_account_id: "", reference: "", receipt_data: "" });

  const load = useCallback(() => {
    api.get("/portal/resumen").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-slate-400 text-sm">Cargando tu portal…</p>;

  const subjectName = (sid) => data.subjects.find((s) => s.id === sid)?.name || "—";
  const pending = data.invoices.filter((i) => i.status === "pendiente");
  const methods = data.school?.payment_methods || ["Efectivo", "Transferencia", "Depósito", "Tarjeta"];

  const openPay = (inv) => {
    setPayInvoice(inv);
    setForm({ method: methods[0] || "Transferencia", bank_account_id: "", reference: "", receipt_data: "" });
  };

  const onReceipt = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("El comprobante no debe superar 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, receipt_data: reader.result }));
    reader.readAsDataURL(file);
  };

  const submitPayment = async () => {
    try {
      await api.post("/payments", { invoice_id: payInvoice.id, ...form, amount: payInvoice.total });
      toast.success("Comprobante enviado. Tu pago está EN REVISIÓN.");
      setPayInvoice(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="parent-portal">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="portal-title">
          Portal de Padres — {data.guardian.name} {data.guardian.last_name}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{data.school?.name}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="portal-stat-hijos">
          <p className="text-2xl font-bold text-slate-900">{data.hijos.length}</p>
          <p className="text-xs text-slate-500">Hijos registrados</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="portal-stat-pendientes">
          <p className="text-2xl font-bold text-rose-600">{pending.length}</p>
          <p className="text-xs text-slate-500">Facturas pendientes</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="portal-stat-pagos">
          <p className="text-2xl font-bold text-emerald-600">{data.payments.filter((p) => p.status === "pagado").length}</p>
          <p className="text-xs text-slate-500">Pagos completados</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="portal-stat-tareas">
          <p className="text-2xl font-bold text-slate-900">{data.assignments.length}</p>
          <p className="text-xs text-slate-500">Tareas activas</p>
        </div>
      </div>

      <Tabs defaultValue="facturas">
        <TabsList className="flex flex-wrap h-auto gap-1 mb-4">
          <TabsTrigger value="hijos" data-testid="ptab-hijos">Hijos</TabsTrigger>
          <TabsTrigger value="facturas" data-testid="ptab-facturas">Facturas</TabsTrigger>
          <TabsTrigger value="pagos" data-testid="ptab-pagos">Pagos</TabsTrigger>
          <TabsTrigger value="tareas" data-testid="ptab-tareas">Tareas</TabsTrigger>
          <TabsTrigger value="notas" data-testid="ptab-notas">Calificaciones</TabsTrigger>
          <TabsTrigger value="actividades" data-testid="ptab-actividades">Actividades</TabsTrigger>
          <TabsTrigger value="menu" data-testid="ptab-menu">Menú</TabsTrigger>
          <TabsTrigger value="bancos" data-testid="ptab-bancos">Cuentas bancarias</TabsTrigger>
        </TabsList>

        <TabsContent value="hijos">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.hijos.map((h) => (
              <div key={h.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`hijo-card-${h.id}`}>
                <p className="font-semibold text-slate-900">{h.first_name} {h.last_name}</p>
                <p className="text-sm text-slate-500 mt-1">{h.grade} · Sección {h.section}</p>
                <div className="mt-2"><StatusBadge value={h.status} /></div>
              </div>
            ))}
            {data.hijos.length === 0 && <p className="text-slate-400 text-sm col-span-full">El colegio aún no ha asociado estudiantes a tu cuenta.</p>}
          </div>
        </TabsContent>

        <TabsContent value="facturas">
          <DataTable testid="portal-invoices-table" rows={data.invoices} columns={[
            { key: "number", label: "Factura", render: (i) => <span className="font-mono text-xs font-semibold">{i.number}</span> },
            { key: "student_name", label: "Estudiante" },
            { key: "issue_date", label: "Fecha" },
            { key: "total", label: "Total", render: (i) => fmt(i.total) },
            { key: "status", label: "Estado", render: (i) => <StatusBadge value={i.status} /> },
            {
              key: "_a", label: "",
              render: (i) => (
                <div className="flex gap-1">
                  {i.status === "pendiente" && (
                    <Button size="sm" data-testid={`pay-invoice-${i.id}`} className="bg-blue-800 hover:bg-blue-700" onClick={() => openPay(i)}>
                      <CreditCard className="w-4 h-4 mr-1" /> Pagar
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" data-testid={`portal-invoice-pdf-${i.id}`} onClick={async () => { try { await downloadPdf(`/invoices/${i.id}/pdf`, `${i.number}.pdf`); } catch (e) { toast.error(formatApiError(e)); } }}>
                    PDF
                  </Button>
                </div>
              ),
            },
          ]} />
        </TabsContent>

        <TabsContent value="pagos">
          <DataTable testid="portal-payments-table" rows={data.payments} columns={[
            { key: "invoice_number", label: "Factura", render: (p) => <span className="font-mono text-xs">{p.invoice_number}</span> },
            { key: "amount", label: "Monto", render: (p) => fmt(p.amount) },
            { key: "method", label: "Método" },
            { key: "reference", label: "Referencia" },
            { key: "status", label: "Estado", render: (p) => <StatusBadge value={p.status} /> },
          ]} />
        </TabsContent>

        <TabsContent value="tareas">
          <DataTable testid="portal-assignments-table" rows={data.assignments} columns={[
            { key: "title", label: "Tarea" },
            { key: "subject_id", label: "Materia", render: (a) => subjectName(a.subject_id) },
            { key: "course", label: "Curso" },
            { key: "due_date", label: "Entrega" },
            { key: "description", label: "Descripción" },
          ]} />
        </TabsContent>

        <TabsContent value="notas">
          <DataTable testid="portal-grades-table" rows={data.grades} columns={[
            { key: "subject_id", label: "Materia", render: (g) => subjectName(g.subject_id) },
            { key: "evaluation", label: "Evaluación" },
            { key: "period", label: "Período" },
            { key: "score", label: "Nota", render: (g) => <span className="font-mono font-semibold">{g.score}</span> },
          ]} />
        </TabsContent>

        <TabsContent value="actividades">
          <DataTable testid="portal-activities-table" rows={data.activities} columns={[
            { key: "name", label: "Actividad" },
            { key: "date", label: "Fecha" },
            { key: "time", label: "Hora" },
            { key: "place", label: "Lugar" },
            { key: "cost", label: "Costo", render: (a) => (a.cost ? fmt(a.cost) : "Gratis") },
          ]} />
        </TabsContent>

        <TabsContent value="bancos">
          <div className="grid sm:grid-cols-2 gap-4">
            {data.bank_accounts.map((b) => (
              <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={`bank-card-${b.id}`}>
                <div className="flex items-center gap-2 mb-2"><Landmark className="w-4 h-4 text-blue-800" /><p className="font-semibold text-slate-900">{b.bank}</p></div>
                <p className="text-sm text-slate-600">{b.name} — {b.account_type}</p>
                <p className="font-mono text-sm text-slate-800 mt-1">{b.account_number}</p>
                <p className="text-xs text-slate-500 mt-1">Beneficiario: {b.beneficiary}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="menu">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="portal-menu">
            {(() => {
              const DAYS_ORDER = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
              const MEALS_ORDER = ["Desayuno", "Almuerzo", "Merienda"];
              const menus = (data.menus || []).sort(
                (a, b) => (a.date || "").localeCompare(b.date || "") || DAYS_ORDER.indexOf(a.day) - DAYS_ORDER.indexOf(b.day) || MEALS_ORDER.indexOf(a.meal_type) - MEALS_ORDER.indexOf(b.meal_type)
              );
              if (menus.length === 0) return <p className="text-sm text-slate-400 text-center py-8">El colegio aún no ha publicado el menú.</p>;
              const byDay = {};
              menus.forEach((m) => { (byDay[`${m.day} ${m.date}`] = byDay[`${m.day} ${m.date}`] || []).push(m); });
              return Object.entries(byDay).map(([dayLabel, meals]) => (
                <div key={dayLabel} className="border-b border-slate-100 px-5 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">{dayLabel}</p>
                  <div className="flex flex-wrap gap-2">
                    {meals.map((m) => (
                      <span key={m.id} className="text-xs px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                        <strong>{m.meal_type}:</strong> {m.dish}
                      </span>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!payInvoice} onOpenChange={() => setPayInvoice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Pagar {payInvoice?.number} — {payInvoice ? fmt(payInvoice.total) : ""}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Modalidad de pago</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(form.method === "Transferencia" || form.method === "Depósito") && (
              <div className="space-y-1.5">
                <Label>Cuenta bancaria del colegio</Label>
                <Select value={form.bank_account_id} onValueChange={(v) => setForm({ ...form, bank_account_id: v })}>
                  <SelectTrigger data-testid="pay-bank-select"><SelectValue placeholder="Seleccionar cuenta" /></SelectTrigger>
                  <SelectContent>
                    {data.bank_accounts.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank} — {b.account_number}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.bank_account_id && (() => {
                  const b = data.bank_accounts.find((x) => x.id === form.bank_account_id);
                  return b ? (
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-slate-700" data-testid="bank-details">
                      <p className="font-semibold">{b.bank} · {b.account_type}</p>
                      <p className="font-mono">{b.account_number}</p>
                      <p>Beneficiario: {b.beneficiary}</p>
                    </div>
                  ) : null;
                })()}
              </div>
            )}
            <div className="space-y-1.5"><Label>Referencia / No. de comprobante</Label><Input data-testid="pay-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Comprobante (imagen o PDF, máx. 2MB)</Label>
              <Input type="file" accept="image/*,application/pdf" data-testid="pay-receipt-file" onChange={onReceipt} />
              {form.receipt_data && <p className="text-xs text-emerald-600">Comprobante adjunto ✓</p>}
            </div>
            <p className="text-xs text-slate-400">Tu pago quedará <strong>EN REVISIÓN</strong> hasta que el colegio lo apruebe.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-pay" onClick={() => setPayInvoice(null)}>Cancelar</Button>
            <Button data-testid="submit-pay" className="bg-blue-800 hover:bg-blue-700" onClick={submitPayment}>Enviar para revisión</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
